// src/services/stripeService.js
import { loadStripe } from '@stripe/stripe-js';
import { supabase } from '../lib/supabaseClient';

export const PRICING_PLANS = {
  monthly: {
    priceId: process.env.REACT_APP_STRIPE_MONTHLY_PRICE_ID,
    amount: 49,
    interval: 'month',
    launchPrice: 39,
    launchPriceId: process.env.REACT_APP_STRIPE_LAUNCH_MONTHLY_PRICE_ID
  },
  yearly: {
    priceId: process.env.REACT_APP_STRIPE_YEARLY_PRICE_ID,
    amount: 470,
    interval: 'year',
    launchPrice: 370,
    launchPriceId: process.env.REACT_APP_STRIPE_LAUNCH_YEARLY_PRICE_ID
  }
};

export const createTrialSubscription = async (priceId, userId, email) => {
  try {
    const { data, error } = await supabase.functions.invoke('create-trial-subscription', {
      body: {
        priceId,
        userId,
        email,
        trialPeriodDays: 14,
        successUrl: `${window.location.origin}/trial-success`,
        cancelUrl: `${window.location.origin}/pricing`
      }
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error('Error creating trial subscription:', error);
    throw error;
  }
};

export const createUpgradeSubscription = async (priceId, userId, email) => {
  try {
    const { data, error } = await supabase.functions.invoke('create-subscription-upgrade', {
      body: {
        priceId,
        userId,
        email,
        successUrl: `${window.location.origin}/dashboard?upgrade=success&refresh=true`,
        cancelUrl: `${window.location.origin}/dashboard` // Changed from /pricing
      }
    });

    if (error) {
      // Try to parse the error for more details
      console.error('Edge function error details:', error);
      
      // If the error has a message property, use it
      if (error.message) {
        throw new Error(error.message);
      }
      
      throw error;
    }
    
    // Check if data contains an error
    if (data && data.error) {
      console.error('Edge function returned error in data:', data.error);
      throw new Error(data.error);
    }
    
    return data;
  } catch (error) {
    console.error('Error creating subscription upgrade:', error);
    throw error;
  }
};

export const redirectToCheckout = async (sessionId) => {
  const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  const { error } = await stripe.redirectToCheckout({ sessionId });
  
  if (error) {
    console.error('Error redirecting to checkout:', error);
    throw error;
  }
};

export const getSubscriptionStatus = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('business_profiles')
      .select(`
        subscription_status, 
        subscription_plan, 
        trial_start_date,
        trial_end_date,
        subscription_end_date,
        has_used_trial
      `)
      .eq('user_id', userId)
      .single();

    if (error) throw error;
    
    // Calculate days remaining in trial
    if (data.subscription_status === 'trial' && data.trial_end_date) {
      const trialEnd = new Date(data.trial_end_date);
      const now = new Date();
      const daysRemaining = Math.floor((trialEnd - now) / (1000 * 60 * 60 * 24));
      
      return {
        ...data,
        daysRemaining: Math.max(0, daysRemaining),
        isTrialExpired: daysRemaining < 0
      };
    }
    
    return data;
  } catch (error) {
    console.error('Error getting subscription status:', error);
    return null;
  }
};

export const checkTrialEligibility = async (userId) => {
  try {
    const { data, error } = await supabase
      .from('business_profiles')
      .select('has_used_trial')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') throw error;
    
    // New user or hasn't used trial yet
    return !data?.has_used_trial;
  } catch (error) {
    console.error('Error checking trial eligibility:', error);
    return false;
  }
};